import React, { useEffect, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  // flexRender,
} from "@tanstack/react-table";
import {
  // ChevronDown,
  // ChevronLeft,
  // ChevronRight,
  // ChevronUp,
  Search,
  X,
} from "lucide-react";
import { DataGrid } from "../DataGrid";
import {
  Card,
  CardFooter,
  CardHeader,
  CardHeading,
  CardTable,
  CardTitle,
  CardToolbar,
} from "../Card";
import { Input } from "../Input";
import { Button } from "../Button";
import { ScrollArea, ScrollBar } from "../ScrollArea";
// import { DataGridPagination } from "../DataGridPagination";
import { DataGridTable } from "../DataGridTable";
// import GlobalPagination from "utils/GlobalPagination";
import { PaginationComponent } from "components/atoms";
import { DataGridPagination } from "../DataGridPagination";

const TableContainer = ({
  columns,
  data,
  title = "",
  isPagination = true,
  recordCount = null,
  isGlobalFilter = true,
  onSearch = () => { },
  onUpdatePagination = () => { },
  onUpdateSort = () => { },
  extraButton = <></>,
  hasSearch = false,
  totalPages,
  paginationState = { pageIndex: 0, pageSize: 10 },
  customSorting = false,
}) => {
  const [pagination, setPagination] = useState(
    !isPagination ? paginationState : { pageIndex: 0, pageSize: 10 }
  );
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState([{ id: "lastModified", desc: true }]);

  const table = useReactTable({
    columns,
    data: data,
    pageCount: !isPagination
      ? Math.ceil(
        (recordCount ? recordCount : data?.length ? data?.length : 0) /
        paginationState?.pageSize
      )
      : Math.ceil((data?.length || 0) / pagination.pageSize),
    getRowId: (row) => row.id,
    state: !isPagination
      ? {
        pagination: paginationState,
        sorting,
      }
      : {
        pagination,
        sorting,
        rowSelection,
      },
    // onSortingChange: (e) => {
    //   setSorting(e);
    //   if (customSorting) {
    //     onUpdateSort(e());
    //   }
    // },
    columnResizeMode: "onChange",
    onSortingChange: !isPagination
      ? (updater) => {
        setSorting(updater);

        const nextSort =
          typeof updater === "function" ? updater(sorting) : updater;

        if (customSorting) {
          if (nextSort.length > 0) {
            const sortedColumn = columns.find(
              (col) => col.id === nextSort[0]?.id
            );
            if (sortedColumn?.enableSorting) {
              onUpdateSort(nextSort);
            }
          } else {
            const lastSortedColumn = columns.find(
              (col) => col.id === sorting?.[0]?.id
            );

            if (lastSortedColumn?.enableSorting) {
              onUpdateSort([]);
            }
          }
        }
      }
      : setSorting,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: !isPagination,
    manualSorting: true,
  });

  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (onSearch) {
        onSearch(searchQuery.trim());
      }
    }, 800);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  return (
    <DataGrid
      table={table}
      // recordCount={recordCount ? recordCount : data?.length ? data?.length : 0}
      recordCount={recordCount ?? data?.length ?? 0}
      tableLayout={{
        columnsPinnable: true,
        columnsMovable: true,
        columnsVisibility: false,
        cellBorder: true,
      }}
    >
      <Card className="min-w-full">
        <CardHeader className="flex flex-wrap items-center justify-between gap-3 py-3">
          <CardHeading className="flex min-w-0 flex-wrap items-center gap-3">
            <CardTitle className="flex min-w-0 flex-wrap items-center gap-3">
              {title && <span>{title}</span>}

              {hasSearch && (
                <div className="relative min-w-0 w-full sm:w-50">
                  <Search className="size-4 z-10 text-muted-foreground absolute start-3 top-1/2 -translate-y-1/2" />

                  <Input
                    placeholder={`Search ${title}`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    inputClassName="px-8"
                    className="w-full"
                  />

                  {searchQuery.length > 0 && (
                    <Button
                      mode="icon"
                      variant="ghost"
                      className="absolute end-1.5 top-1/2 -translate-y-1/2 h-6 w-6"
                      onClick={() => setSearchQuery("")}
                    >
                      <X />
                    </Button>
                  )}
                </div>
              )}
            </CardTitle>
          </CardHeading>

          <CardToolbar className="flex min-w-0 flex-wrap items-center gap-3">
            {isGlobalFilter && (
              <div className="relative min-w-0 w-full sm:w-40">
                <Search className="size-4 text-muted-foreground absolute start-3 top-1/2 -translate-y-1/2" />

                <Input
                  placeholder="Search by id"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="ps-9 w-full"
                />

                {searchQuery.length > 0 && (
                  <Button
                    mode="icon"
                    variant="ghost"
                    className="absolute end-1.5 top-1/2 -translate-y-1/2 h-6 w-6"
                    onClick={() => setSearchQuery("")}
                  >
                    <X />
                  </Button>
                )}
              </div>
            )}

            {extraButton}
          </CardToolbar>
        </CardHeader>
        <CardTable>
          <ScrollArea>
            <DataGridTable />
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </CardTable>
        <CardFooter>
          {isPagination ? (
            <DataGridPagination />
          ) : (
            <PaginationComponent
              limit={paginationState?.pageSize}
              currentPageNo={paginationState?.pageIndex + 1}
              onChangePagination={(size, page) => {
                onUpdatePagination(size, page);
              }}
              // total={recordCount}
              total={recordCount ?? 0}
              totalPage={totalPages}
            />
          )}
        </CardFooter>
      </Card>
    </DataGrid>
  );
};

export default TableContainer;
